import React, { useEffect, useMemo, useRef, useState, CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { I } from '../icons-ds';
import { Button, IconButton, copyPlainText } from '../ui';
import { useToast } from '../lib/ToastContext';
import { useViewport } from '../layout';
import {
  parseTemplate, renderSegments, inputFields,
  type Segment, type PlaceholderField, type FieldValues,
} from '../lib/placeholders';
import type { Phrase } from '../lib/types';

export type NoteBuilderProps = { phrase: Phrase; onClose: () => void };

type ParsedSection = { title: string; segments: Segment[] };

// Generic auto-labels carry no real meaning, so we don't echo them as a hint
// inside the slot — the surrounding sentence is the label.
const GENERIC_LABELS = new Set(['Fill in', 'Choose one', 'Select all that apply']);
const hintFor = (f: PlaceholderField) => (GENERIC_LABELS.has(f.label) ? '' : f.label);

const menuItemStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
  padding: '8px 10px', borderRadius: 8, border: 0, background: 'transparent',
  color: 'var(--fg-primary)', fontSize: 14, cursor: 'pointer',
};

// ---- Chart-pull (auto-filled by Epic) -----------------------------------
function AutoChip({ label }: { label: string }) {
  return (
    <span
      title="Auto-filled by Epic from the chart"
      style={{
        display: 'inline-flex', alignItems: 'center', margin: '0 1px',
        padding: '0 6px', borderRadius: 5, verticalAlign: 'baseline',
        whiteSpace: 'nowrap', fontFamily: 'var(--font-sans)',
        fontSize: '0.82em', fontWeight: 500, color: 'var(--fg-tertiary)',
        background: 'color-mix(in oklab, var(--fg-tertiary) 14%, transparent)',
      }}
    >
      {label}
    </span>
  );
}

// ---- Free-text blank (grows with content, highlighted while empty) ------
function FillText({
  value, hint, empty, faint, onChange, onEnter, register,
}: {
  value: string;
  hint: string;
  empty: boolean;
  faint: boolean;
  onChange: (v: string) => void;
  onEnter: () => void;
  register: (el: HTMLElement | null) => void;
}) {
  // data-value drives the auto-sizing ::after mirror (see base.css .dp-fill).
  // Cap the mirror to the hint's first chars so a long guidance note can't blow
  // out the line; the full hint still shows as the (clipping) placeholder.
  const sizing = value || hint.slice(0, 32);
  return (
    <span className="dp-fill" data-value={sizing || ''}>
      <input
        ref={register}
        className="dp-fill-input"
        data-empty={empty ? '' : undefined}
        data-caption={faint ? '' : undefined}
        value={value}
        placeholder={hint}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onEnter();
          }
        }}
        enterKeyHint="next"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
      />
    </span>
  );
}

// ---- Pick-list blank (inline chip → portal popover) ---------------------
function FillChoice({
  field, value, onChange, hint, register,
}: {
  field: PlaceholderField;
  value: string | string[] | undefined;
  onChange: (v: string | string[]) => void;
  hint: string;
  register: (el: HTMLElement | null) => void;
}) {
  const multi = field.kind === 'multi';
  const opts = field.options || [];
  const selectedArr = Array.isArray(value) ? value : [];
  const selectedStr = typeof value === 'string' ? value : '';
  const hasVal = multi ? selectedArr.length > 0 : Boolean(selectedStr);
  const display = multi
    ? (selectedArr.length ? selectedArr.join(', ') : hint || 'Select…')
    : (selectedStr || hint || 'Choose…');

  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number } | null>(null);
  const open = pos !== null;
  const close = () => setPos(null);

  const openMenu = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const openUp = r.bottom > window.innerHeight * 0.62;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - 290));
    setPos(openUp
      ? { left, bottom: window.innerHeight - r.top + 4 }
      : { left, top: r.bottom + 4 });
  };

  // A portal popover doesn't move with the page, so close it on scroll/resize.
  useEffect(() => {
    if (!open) return;
    const onMove = () => close();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);

  const choose = (opt: string) => {
    if (multi) {
      onChange(selectedArr.includes(opt) ? selectedArr.filter((o) => o !== opt) : [...selectedArr, opt]);
    } else {
      onChange(selectedStr === opt ? '' : opt);
      close();
    }
  };

  const setRef = (el: HTMLButtonElement | null) => {
    btnRef.current = el;
    register(el);
  };

  return (
    <span style={{ display: 'inline-block', verticalAlign: 'baseline', margin: '0 1px', maxWidth: '100%' }}>
      <button
        ref={setRef}
        type="button"
        onClick={() => (open ? close() : openMenu())}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%',
          font: 'inherit', padding: '1px 4px 1px 6px', borderRadius: 6, border: 0, cursor: 'pointer',
          background: hasVal ? 'transparent' : 'var(--accent-fill)',
          boxShadow: hasVal ? 'inset 0 -1px 0 var(--line-border)' : 'inset 0 -1.5px 0 var(--accent)',
          color: hasVal ? 'var(--fg-primary)' : 'var(--accent)',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
          {display}
        </span>
        <I.chevDown size={12} style={{ flexShrink: 0, opacity: 0.7 }} />
      </button>

      {open && pos && createPortal(
        <>
          <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 80 }} />
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed', left: pos.left, top: pos.top, bottom: pos.bottom, zIndex: 81,
              minWidth: 190, maxWidth: 280, maxHeight: 280, overflowY: 'auto',
              background: 'var(--bg-surface)', border: '1px solid var(--line-divider)',
              borderRadius: 'var(--r-md)', boxShadow: 'var(--shadow-3)', padding: 4,
            }}
          >
            {opts.map((opt) => {
              const on = multi ? selectedArr.includes(opt) : selectedStr === opt;
              return (
                <button key={opt} type="button" className="dp-menu-item" onClick={() => choose(opt)} style={menuItemStyle}>
                  <span style={{ width: 16, flexShrink: 0, color: 'var(--accent)', display: 'inline-flex' }}>
                    {on && <I.check size={14} stroke={3} />}
                  </span>
                  <span style={{ flex: 1 }}>{opt}</span>
                </button>
              );
            })}
            {multi && (
              <button
                type="button"
                onClick={close}
                style={{ ...menuItemStyle, justifyContent: 'center', color: 'var(--accent)', fontWeight: 600 }}
              >
                Done
              </button>
            )}
          </div>
        </>,
        document.body
      )}
    </span>
  );
}

export function NoteBuilder({ phrase, onClose }: NoteBuilderProps) {
  const { showToast } = useToast();
  const isMobile = useViewport() === 'mobile';

  const parsed: ParsedSection[] = useMemo(
    () => phrase.sections.map((s, i) => ({ title: s.title, segments: parseTemplate(s.body, `s${i}`) })),
    [phrase]
  );

  const allFields = useMemo(() => parsed.flatMap((s) => inputFields(s.segments)), [parsed]);

  const [values, setValues] = useState<FieldValues>(() => {
    const init: FieldValues = {};
    allFields.forEach((f) => {
      if (f.kind === 'text' && f.default) init[f.key] = f.default;
    });
    return init;
  });

  // Focusable element per field, in document order — drives "Next blank".
  const els = useRef(new Map<string, HTMLElement | null>());
  const register = (key: string) => (el: HTMLElement | null) => {
    els.current.set(key, el);
  };

  // Lock background scroll + close on Escape while the modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const isEmptyKey = (key: string) => {
    const v = values[key];
    return Array.isArray(v) ? v.length === 0 : !(typeof v === 'string' && v.trim());
  };

  const total = allFields.length;
  const remaining = allFields.filter((f) => isEmptyKey(f.key)).length;
  const filled = total - remaining;

  const focusNext = (afterKey?: string) => {
    const keys = allFields.map((f) => f.key);
    if (!keys.length) return;
    const start = afterKey ? keys.indexOf(afterKey) + 1 : 0;
    for (let i = 0; i < keys.length; i++) {
      const key = keys[(start + i) % keys.length];
      if (isEmptyKey(key)) {
        const el = els.current.get(key);
        if (el) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
          el.focus({ preventScroll: true });
        }
        return;
      }
    }
  };

  const setValue = (key: string, v: string | string[]) => setValues((prev) => ({ ...prev, [key]: v }));

  const filledNote = () =>
    parsed.map((s) => `# ${s.title}\n\n${renderSegments(s.segments, values, 'filled')}`).join('\n\n');

  const copyFilled = async () => {
    if (await copyPlainText(filledNote())) {
      showToast(remaining === 0 ? 'Filled note copied' : `Copied — ${remaining} blank(s) still empty`);
    }
  };

  const reset = () => {
    const init: FieldValues = {};
    allFields.forEach((f) => {
      if (f.kind === 'text' && f.default) init[f.key] = f.default;
    });
    setValues(init);
  };

  const renderSeg = (seg: Segment, i: number) => {
    if (seg.type === 'literal') return <span key={`l${i}`}>{seg.text}</span>;
    if (seg.type === 'tip') return null;
    const f = seg.field;
    if (f.kind === 'chartpull') return <AutoChip key={f.key} label={f.label} />;
    if (f.kind === 'choice' || f.kind === 'multi') {
      return (
        <FillChoice
          key={f.key}
          field={f}
          value={values[f.key]}
          hint={hintFor(f)}
          onChange={(v) => setValue(f.key, v)}
          register={register(f.key)}
        />
      );
    }
    return (
      <FillText
        key={f.key}
        value={typeof values[f.key] === 'string' ? (values[f.key] as string) : ''}
        hint={hintFor(f)}
        empty={isEmptyKey(f.key)}
        faint={Boolean(f.inferred)}
        onChange={(v) => setValue(f.key, v)}
        onEnter={() => focusNext(f.key)}
        register={register(f.key)}
      />
    );
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center',
        padding: isMobile ? 0 : 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="anim-scale"
        style={{
          display: 'flex', flexDirection: 'column',
          width: '100%', maxWidth: 760,
          height: isMobile ? '100%' : '92vh',
          background: 'var(--bg-surface)',
          borderRadius: isMobile ? 0 : 'var(--r-xl, 20px)',
          border: '1px solid var(--line-divider)', overflow: 'hidden',
          boxShadow: 'var(--shadow-3)',
        }}
      >
        {/* Header — extends into the status-bar area on standalone mobile. */}
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            padding: isMobile ? 'calc(14px + env(safe-area-inset-top, 0px)) 16px 14px' : '14px 16px',
            borderBottom: '1px solid var(--line-divider)', background: 'var(--bg-grouped)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <I.sparkles size={18} stroke={2} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--fg-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Fill in · {phrase.name}
              </div>
              <div className="t-caption1" style={{ color: 'var(--fg-tertiary)' }}>
                {filled} of {total} filled
              </div>
            </div>
          </div>
          <IconButton icon={<I.close size={20} />} label="Close" onClick={onClose} />
        </div>

        {/* The note itself — fill the blanks in place. */}
        <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <div style={{ maxWidth: 680, margin: '0 auto', padding: isMobile ? '16px' : '24px 28px' }}>
            {parsed.map((s, si) => (
              <div key={si} style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
                  {s.title}
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-mono)', fontSize: 16, lineHeight: 2.0,
                    color: 'var(--fg-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}
                >
                  {s.segments.map((seg, i) => renderSeg(seg, i))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer — clears the home indicator on standalone mobile. */}
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            padding: isMobile ? '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))' : '12px 16px',
            borderTop: '1px solid var(--line-divider)', background: 'var(--bg-grouped)',
          }}
        >
          <Button variant="plain" size="sm" icon={<I.trash size={15} />} onClick={reset}>
            Reset
          </Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {remaining > 0 && (
              <Button variant="tinted" size="sm" iconRight={<I.chevDown size={14} />} onClick={() => focusNext()}>
                {isMobile ? `Next · ${remaining}` : `Next blank · ${remaining} left`}
              </Button>
            )}
            <Button variant="primary" size={isMobile ? 'sm' : 'md'} icon={<I.copy size={16} />} onClick={copyFilled}>
              {isMobile ? 'Copy' : 'Copy filled note'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NoteBuilder;
